import React, { useState, useEffect, useCallback } from 'react';

const DEFAULT_WIDGET_CONFIG = {
  overlayWidgets: [
    { key: 'timer', enabled: true },
    { key: 'kanban', enabled: true },
    { key: 'rotatingStats', enabled: true }
  ],
  rotatingStats: {
    cycleDurationMs: 6000,
    items: [
      { key: 'lastSub', enabled: true, label: 'LAST SUB' },
      { key: 'lastFollow', enabled: true, label: 'LAST FOLLOW' },
      { key: 'topCheer', enabled: true, label: 'TOP CHEER' },
      { key: 'checkInStreak', enabled: true, label: 'CHECK-IN STREAK' },
      { key: 'subs', enabled: true, label: 'SUBS' },
      { key: 'followers', enabled: true, label: 'FOLLOWERS' },
      { key: 'firstToday', enabled: true, label: 'FIRST TODAY' }
    ]
  },
  appearance: {
    labelColor: '#d4c4a8',
    valueColor: '#f5f0e6',
    accentColor: '#e8b298',
    progressBarColor: '#a65241'
  }
};

const WIDGET_LABELS = {
  timer: 'Stream Timer',
  kanban: 'Working On (Kanban)',
  rotatingStats: 'Rotating Stats'
};

const STAT_DESCRIPTIONS = {
  lastSub: 'Most recent subscriber',
  lastFollow: 'Most recent follower',
  topCheer: 'Top cheerer with bits',
  checkInStreak: 'Longest check-in streak',
  subs: 'Subscription goal progress',
  followers: 'Follower goal progress',
  firstToday: 'First channel point redeemer'
};

export default function AdminWidgetConfig({ onLogout }) {
  const [config, setConfig] = useState(null);
  const [overlayTheme, setOverlayTheme] = useState('flannel');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [themeSaving, setThemeSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [themeMessage, setThemeMessage] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [savedConfig, setSavedConfig] = useState(null);

  const fetchOverlayTheme = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) { onLogout(); return; }

      const response = await fetch('/api/admin?action=get-overlay-theme', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) { onLogout(); return; }

      const data = await response.json();
      if (data.success) {
        setOverlayTheme(data.theme || 'flannel');
      }
    } catch (err) {
      console.error('Failed to load overlay theme:', err);
    }
  }, [onLogout]);

  const setTheme = async (theme) => {
    if (theme === overlayTheme || themeSaving) return;

    try {
      setThemeSaving(true);
      setError('');
      setThemeMessage('');
      const token = localStorage.getItem('adminToken');
      if (!token) { onLogout(); return; }

      const response = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'set-overlay-theme', theme })
      });

      if (response.status === 401) { onLogout(); return; }

      const data = await response.json();
      if (data.success) {
        setOverlayTheme(data.theme);
        setThemeMessage(`Overlay theme set to ${data.theme === 'summer' ? 'Summer Party' : 'Flannel'}. OBS sources update within a few seconds.`);
        setTimeout(() => setThemeMessage(''), 4000);
      } else {
        setError(data.error || 'Failed to set overlay theme');
      }
    } catch (err) {
      setError('Failed to set overlay theme');
    } finally {
      setThemeSaving(false);
    }
  };

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('adminToken');
      if (!token) { onLogout(); return; }

      const response = await fetch('/api/admin?action=get-widget-config', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) { onLogout(); return; }

      const data = await response.json();
      if (data.success) {
        setConfig(data.config);
        setSavedConfig(JSON.stringify(data.config));
        setHasChanges(false);
      } else {
        setError(data.error || 'Failed to load widget config');
      }
    } catch (err) {
      setError('Failed to load widget config');
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { fetchConfig(); fetchOverlayTheme(); }, [fetchConfig, fetchOverlayTheme]);

  useEffect(() => {
    if (config && savedConfig) {
      setHasChanges(JSON.stringify(config) !== savedConfig);
    }
  }, [config, savedConfig]);

  const saveConfig = async () => {
    try {
      setSaving(true);
      setError('');
      setSaveMessage('');
      const token = localStorage.getItem('adminToken');
      if (!token) { onLogout(); return; }

      const response = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'set-widget-config', config })
      });

      if (response.status === 401) { onLogout(); return; }

      const data = await response.json();
      if (data.success) {
        setSavedConfig(JSON.stringify(config));
        setHasChanges(false);
        setSaveMessage('Configuration saved!');
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        setError(data.error || 'Failed to save config');
      }
    } catch (err) {
      setError('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setConfig(JSON.parse(JSON.stringify(DEFAULT_WIDGET_CONFIG)));
  };

  const revertChanges = () => {
    if (savedConfig) {
      setConfig(JSON.parse(savedConfig));
    }
  };

  const toggleWidget = (key) => {
    setConfig(prev => ({
      ...prev,
      overlayWidgets: prev.overlayWidgets.map(w =>
        w.key === key ? { ...w, enabled: !w.enabled } : w
      )
    }));
  };

  const moveWidget = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= config.overlayWidgets.length) return;
    setConfig(prev => {
      const widgets = [...prev.overlayWidgets];
      [widgets[index], widgets[newIndex]] = [widgets[newIndex], widgets[index]];
      return { ...prev, overlayWidgets: widgets };
    });
  };

  const toggleStat = (key) => {
    setConfig(prev => ({
      ...prev,
      rotatingStats: {
        ...prev.rotatingStats,
        items: prev.rotatingStats.items.map(item =>
          item.key === key ? { ...item, enabled: !item.enabled } : item
        )
      }
    }));
  };

  const updateStatLabel = (key, label) => {
    setConfig(prev => ({
      ...prev,
      rotatingStats: {
        ...prev.rotatingStats,
        items: prev.rotatingStats.items.map(item =>
          item.key === key ? { ...item, label } : item
        )
      }
    }));
  };

  const moveStat = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= config.rotatingStats.items.length) return;
    setConfig(prev => {
      const items = [...prev.rotatingStats.items];
      [items[index], items[newIndex]] = [items[newIndex], items[index]];
      return { ...prev, rotatingStats: { ...prev.rotatingStats, items } };
    });
  };

  const setCycleDuration = (ms) => {
    setConfig(prev => ({
      ...prev,
      rotatingStats: { ...prev.rotatingStats, cycleDurationMs: ms }
    }));
  };

  const setAppearanceColor = (key, color) => {
    setConfig(prev => ({
      ...prev,
      appearance: { ...prev.appearance, [key]: color }
    }));
  };

  const resetAppearance = () => {
    setConfig(prev => ({
      ...prev,
      appearance: { ...DEFAULT_WIDGET_CONFIG.appearance }
    }));
  };

  if (loading) {
    return (
      <div className="retro-card p-4 border-2 border-retro-cyan mb-4">
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-retro-cyan border-t-transparent"></div>
          <p className="text-retro-muted mt-2">Loading widget config...</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="retro-card p-4 border-2 border-red-500 mb-4">
        <p className="text-red-300">{error || 'Failed to load config'}</p>
        <button onClick={fetchConfig} className="mt-2 px-3 py-1 bg-retro-cyan text-retro-bg font-semibold rounded hover:bg-retro-cyan/80 transition-all duration-200">
          Retry
        </button>
      </div>
    );
  }

  const enabledStatsCount = config.rotatingStats.items.filter(i => i.enabled).length;

  return (
    <div className="retro-card p-4 border-2 border-teal-500 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-retro-text">Widget Configuration</h3>
        <div className="flex gap-2">
          <button
            onClick={fetchConfig}
            disabled={saving}
            className="px-3 py-1 bg-teal-600 text-white font-semibold rounded hover:bg-teal-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border-2 border-red-500 rounded-lg p-3 mb-3">
          <p className="text-red-300">{error}</p>
        </div>
      )}

      {saveMessage && (
        <div className="bg-green-900/20 border-2 border-green-500 rounded-lg p-3 mb-3">
          <p className="text-green-300">{saveMessage}</p>
        </div>
      )}

      {themeMessage && (
        <div className="bg-green-900/20 border-2 border-green-500 rounded-lg p-3 mb-3">
          <p className="text-green-300">{themeMessage}</p>
        </div>
      )}

      {/* Overlay Theme */}
      <div className="mb-6 p-4 bg-retro-surface rounded border border-teal-600/40">
        <h4 className="text-md font-semibold text-teal-400 uppercase mb-2">Overlay Theme</h4>
        <p className="text-xs text-retro-muted mb-3">
          Switch between Flannel and Summer Party without changing your OBS browser-source URLs.
          The overlay polls every few seconds and updates automatically.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setTheme('flannel')}
            disabled={themeSaving}
            className={`px-4 py-2 rounded font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
              overlayTheme === 'flannel'
                ? 'bg-teal-500 text-retro-bg ring-2 ring-teal-300'
                : 'bg-retro-bg text-retro-muted hover:text-retro-text border border-gray-600'
            }`}
          >
            Flannel
          </button>
          <button
            type="button"
            onClick={() => setTheme('summer')}
            disabled={themeSaving}
            className={`px-4 py-2 rounded font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
              overlayTheme === 'summer'
                ? 'bg-amber-500 text-retro-bg ring-2 ring-amber-300'
                : 'bg-retro-bg text-retro-muted hover:text-retro-text border border-gray-600'
            }`}
          >
            Summer Party
          </button>
        </div>
        <p className="text-xs text-retro-muted mt-2">
          Active theme: <span className="text-teal-400 font-semibold">{overlayTheme === 'summer' ? 'Summer Party' : 'Flannel'}</span>
          {themeSaving && <span className="ml-2 text-retro-muted">Saving...</span>}
        </p>
      </div>

      {/* Overlay Widgets */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-teal-400 uppercase mb-3">Overlay Widgets</h4>
        <p className="text-xs text-retro-muted mb-3">
          Toggle widgets on/off and reorder them. Order determines left-to-right placement in the overlay.
        </p>
        <div className="space-y-2">
          {config.overlayWidgets.map((widget, index) => (
            <div key={widget.key} className="flex items-center gap-3 p-3 bg-retro-surface rounded">
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => moveWidget(index, -1)}
                  disabled={index === 0}
                  className="text-xs px-1.5 py-0.5 bg-retro-bg text-retro-muted rounded hover:text-retro-cyan disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Move up"
                >
                  ▲
                </button>
                <button
                  onClick={() => moveWidget(index, 1)}
                  disabled={index === config.overlayWidgets.length - 1}
                  className="text-xs px-1.5 py-0.5 bg-retro-bg text-retro-muted rounded hover:text-retro-cyan disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Move down"
                >
                  ▼
                </button>
              </div>
              <span className="text-xs text-retro-muted font-mono w-5">{index + 1}.</span>
              <label className="flex items-center gap-3 flex-1 cursor-pointer select-none">
                <div
                  onClick={() => toggleWidget(widget.key)}
                  className={`w-10 h-5 rounded-full relative transition-colors duration-200 cursor-pointer ${
                    widget.enabled ? 'bg-teal-500' : 'bg-gray-600'
                  }`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                    widget.enabled ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </div>
                <span className={`font-semibold ${widget.enabled ? 'text-retro-text' : 'text-retro-muted line-through'}`}>
                  {WIDGET_LABELS[widget.key]}
                </span>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Rotating Stats Configuration */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-teal-400 uppercase mb-3">Rotating Stats</h4>
        <p className="text-xs text-retro-muted mb-3">
          Configure which stats rotate in the stats panel, their labels, and display order.
          {enabledStatsCount === 0 && (
            <span className="text-yellow-400 ml-1">Warning: No stats enabled!</span>
          )}
        </p>

        {/* Cycle Duration */}
        <div className="mb-4 p-3 bg-retro-surface rounded">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-retro-text">Cycle Duration</label>
            <span className="text-sm font-mono text-teal-400">
              {(config.rotatingStats.cycleDurationMs / 1000).toFixed(1)}s
            </span>
          </div>
          <input
            type="range"
            min="1000"
            max="30000"
            step="500"
            value={config.rotatingStats.cycleDurationMs}
            onChange={(e) => setCycleDuration(parseInt(e.target.value))}
            className="w-full accent-teal-500"
          />
          <div className="flex justify-between text-xs text-retro-muted mt-1">
            <span>1s</span>
            <span>15s</span>
            <span>30s</span>
          </div>
        </div>

        {/* Stat Items */}
        <div className="space-y-2">
          {config.rotatingStats.items.map((item, index) => (
            <div key={item.key} className="flex items-center gap-3 p-3 bg-retro-surface rounded">
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => moveStat(index, -1)}
                  disabled={index === 0}
                  className="text-xs px-1.5 py-0.5 bg-retro-bg text-retro-muted rounded hover:text-retro-cyan disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Move up"
                >
                  ▲
                </button>
                <button
                  onClick={() => moveStat(index, 1)}
                  disabled={index === config.rotatingStats.items.length - 1}
                  className="text-xs px-1.5 py-0.5 bg-retro-bg text-retro-muted rounded hover:text-retro-cyan disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Move down"
                >
                  ▼
                </button>
              </div>
              <div
                onClick={() => toggleStat(item.key)}
                className={`w-10 h-5 rounded-full relative transition-colors duration-200 cursor-pointer flex-shrink-0 ${
                  item.enabled ? 'bg-teal-500' : 'bg-gray-600'
                }`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                  item.enabled ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </div>
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  value={item.label}
                  onChange={(e) => updateStatLabel(item.key, e.target.value)}
                  maxLength={30}
                  className={`w-full px-2 py-1 bg-retro-bg border border-retro-cyan/30 rounded text-sm font-semibold focus:outline-none focus:border-teal-400 transition-colors ${
                    item.enabled ? 'text-retro-text' : 'text-retro-muted'
                  }`}
                />
                <p className="text-xs text-retro-muted mt-1">{STAT_DESCRIPTIONS[item.key]}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Appearance */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-md font-semibold text-teal-400 uppercase">Appearance</h4>
          <button
            onClick={resetAppearance}
            className="px-2 py-1 text-xs bg-retro-bg text-retro-muted rounded hover:text-retro-text transition-colors"
          >
            Reset Colors
          </button>
        </div>
        <p className="text-xs text-retro-muted mb-3">
          Customize the rotating stats panel colors.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { key: 'labelColor', label: 'Label Color', desc: 'Stat name text' },
            { key: 'valueColor', label: 'Value Color', desc: 'Stat value text' },
            { key: 'accentColor', label: 'Accent Color', desc: 'Extra info text' },
            { key: 'progressBarColor', label: 'Progress Bar', desc: 'Goal bar fill' }
          ].map(({ key, label, desc }) => (
            <div key={key} className="flex items-center gap-3 p-3 bg-retro-surface rounded">
              <input
                type="color"
                value={config.appearance[key]}
                onChange={(e) => setAppearanceColor(key, e.target.value)}
                className="w-8 h-8 rounded cursor-pointer border border-retro-cyan/30 bg-transparent"
              />
              <div>
                <div className="text-sm font-semibold text-retro-text">{label}</div>
                <div className="text-xs text-retro-muted">{desc}</div>
              </div>
              <code className="ml-auto text-xs text-retro-muted font-mono">{config.appearance[key]}</code>
            </div>
          ))}
        </div>

        {/* Preview */}
        <div className="mt-3 p-3 bg-retro-bg rounded border border-retro-cyan/20">
          <div className="text-xs text-retro-muted mb-2">Preview:</div>
          <div className="flex flex-col items-center gap-1">
            <span style={{ color: config.appearance.labelColor, fontSize: '0.75rem', fontFamily: 'monospace' }}>
              {config.rotatingStats.items[0]?.label || 'LABEL'}
            </span>
            <span style={{ color: config.appearance.valueColor, fontSize: '1rem', fontWeight: 700, fontFamily: 'monospace' }}>
              SampleUser
            </span>
            <span style={{ color: config.appearance.accentColor, fontSize: '0.65rem', fontFamily: 'monospace' }}>
              extra info
            </span>
            <div className="w-32 h-2 rounded overflow-hidden mt-1" style={{ background: '#0d4a54' }}>
              <div className="h-full rounded" style={{ width: '65%', background: `linear-gradient(90deg, ${config.appearance.progressBarColor}, ${config.appearance.accentColor})` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Save / Reset Buttons */}
      <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-retro-cyan/20">
        <button
          onClick={saveConfig}
          disabled={saving || !hasChanges}
          className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
        <button
          onClick={revertChanges}
          disabled={saving || !hasChanges}
          className="px-4 py-2 bg-retro-bg text-retro-muted border border-retro-cyan/30 font-semibold rounded hover:text-retro-text transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Revert Changes
        </button>
        <button
          onClick={resetToDefaults}
          disabled={saving}
          className="px-4 py-2 bg-retro-bg text-retro-muted border border-orange-500/30 font-semibold rounded hover:text-orange-400 hover:border-orange-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reset to Defaults
        </button>
        {hasChanges && (
          <span className="text-xs text-yellow-400 italic">Unsaved changes</span>
        )}
      </div>
    </div>
  );
}
